import { FC, useEffect, useMemo, useState } from 'react';
import { Form, Formik, FormikErrors } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import LogoIcon from '../../assets/landing/new_logo.png';
import InformationIcon from '../../assets/icons/Info, Infornation.png';
import Flamingo from '../../assets/images/flamingo.png';
import SecondBgImg from '../../assets/images/onboatding-bg-second-image.png';
import ThirdBgImg from '../../assets/images/onboatding-bg-3-image.png';
import FourthBgImg from '../../assets/images/onboatding-bg-fourth-image.png';
import FifthBgImg from '../../assets/images/onboatding-bg-fifth-image.png';
import PhotoaparatBgImg from '../../assets/images/onboatding-bg-6-image.png';
import BikeBgImg from '../../assets/images/bike.png';
import EiffelTowerBgImg from '../../assets/images/eiffel-tower.png';
import CheckedTableIcon from '../../assets/icons/checked_tebale_mark.png';
import CheckedWhiteIcon from '../../assets/icons/checked_white_bg.png';
import RoboHandImg from '../../assets/images/robo-hand.png';

import { useGetLocationsQuery, useGetPlansQuery, useSignUpCompleteMutation } from '../../store/services/authApi';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setCredentials } from '../../store/slices/authSlice';
import { useIsMobile } from '../../hooks/useWindowWidth';

import CustomButton from '../../common/CustomButton';
import CustomSelect from '../../common/CustomSelect';
import CustomInput from '../../common/CustomInput';
import ToggleRadioButton from '../../common/ToggleRadioButton';
import Plans from '../../components/Plans';
import {
  useGetActivityInterestQuery,
  useGetUserQuery,
  useSubscriptionCheckoutMutation,
  useUpdatePlanMutation,
  useUpdateUserAdditionalInfoMutation,
} from '../../store/services/customizeOptionsApi';
import CustomAutoSelect from '../../common/CustomAutoSelect';
import { IPlan } from '../../components/Plans/Plans';
import PlanPrice from '../../components/PlanCard/PlanPrice';
import {
  SoloIcon,
  LikeIcon,
  FamilyIcon,
  FriendsIcon,
  WorkTravelIcon,
  EconomyIcon,
  MidRangeIcon,
  LuxuryIcon,
  DoesntMatterIcon,
  FastPacedIcon,
  BalancedIcon,
  SlowAndRelaxedIcon,
} from '../../components/Icons';
import { generateActiveGradient } from '../../utils/gradientUtils';

import styles from './PersonalizedForm.module.scss';
import { setPlans, setSelectedPlanId } from '../../store/slices/planSlice';
import { setUserInfo } from '../../store/slices/userSlice';

export type Values = {
  username: string;
  gender: string;
  agegroup: string;
  country: string;
  nearestCity: string;
  nearestAirport: string;
  unitsofMeasure: string;
  travel: string;
  collaborate: string;
  plan: number;
  budgetPreferences: string;
  pace: string;
  planningStyle: string;
  travelPurpose: string[];
  destinations: string[];
  interests: string[];
};

const fieldErrorMessage = (error: unknown): string | undefined => (typeof error === 'string' ? error : undefined);
const MAX_SELECTABLE_OPTIONS = 6;
const PENDING_PREMIUM_CHECKOUT_KEY = 'pendingPremiumCheckout';

const PersonalizedForm: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const step = queryParams.get('step') ?? '';
  const isPaymentReturnStep = step === '2';

  const [activeStep, setActiveStep] = useState(0);
  const [countryInput, setCountryInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [airportInput, setAirportInput] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [airportSearch, setAirportSearch] = useState('');
  const [isAwaitingPremiumSync, setIsAwaitingPremiumSync] = useState<boolean>(() => {
    if (!isPaymentReturnStep) return false;

    const pendingPremiumCheckout = localStorage.getItem(PENDING_PREMIUM_CHECKOUT_KEY);
    if (!pendingPremiumCheckout) return false;

    const timestamp = Number(pendingPremiumCheckout);
    return Number.isFinite(timestamp) && Date.now() - timestamp < 10 * 60 * 1000;
  });

  const [steps] = useState(['step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'step7', 'step8', 'step9']);
  const [openModal, setOpenModal] = useState<{ isOpen: boolean; content: IPlan[] | null }>({
    isOpen: false,
    content: null,
  });
  const [acknowledge, setAcknowledge] = useState(false);
  const { data: countries } = useGetLocationsQuery({ search: countrySearch, type: 'country' });
  const { data: cities } = useGetLocationsQuery({ search: citySearch, type: 'city' });
  const { data: airports } = useGetLocationsQuery({ search: airportSearch, type: 'airport' });
  const [signUpComplete] = useSignUpCompleteMutation();
  const { data: primaryTravelPurpose } = useGetActivityInterestQuery('primary_travel_purposes');
  const { data: typeOfDestination } = useGetActivityInterestQuery('destination_types');
  const { data: activityInterest } = useGetActivityInterestQuery('activity_interests');
  const { plans, plan: planId } = useAppSelector(state => state.plan);
  const { userInfo } = useAppSelector(state => state.auth);
  const existingUser = useAppSelector(state => state.user);
  const [updateUserAdditionalInfo] = useUpdateUserAdditionalInfoMutation();
  const { data } = useGetPlansQuery();
  const [updatePlanMutation] = useUpdatePlanMutation();
  const premiumPlanId = data?.find((plan: IPlan) => plan.type === 'Premium')?.id;
  const [subscriptionCheckout] = useSubscriptionCheckoutMutation();
  const { data: userData } = useGetUserQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: isAwaitingPremiumSync ? 5000 : 0,
    }
  );

  useEffect(() => {
    if (data) {
      dispatch(setPlans(data));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (step) {
      const queryStep = step ? Number(step.split('?')[0]) : NaN;
      setActiveStep(Number(queryStep));
    }
  }, [step]);

  useEffect(() => {
    if (!isAwaitingPremiumSync) return;

    if (userData?.is_premium) {
      localStorage.removeItem(PENDING_PREMIUM_CHECKOUT_KEY);
      setIsAwaitingPremiumSync(false);
    }
  }, [isAwaitingPremiumSync, userData?.is_premium]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCountrySearch(countryInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [countryInput]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCitySearch(cityInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [cityInput]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAirportSearch(airportInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [airportInput]);

  const handleModalClose = () => setOpenModal({ isOpen: false, content: null });

  const getAllAdvantages = () => {
    const map = new Map<string, Set<string>>();
    plans?.forEach((plan: IPlan) => {
      plan.advantages?.forEach(({ content, measure }) => {
        if (!map.has(content)) map.set(content, new Set());
        map.get(content)?.add(String(measure));
      });
    });
    return Array.from(map.keys());
  };

  const getMeasure = (plan: any, content: string) => {
    const adv = plan.advantages?.find((a: any) => a.content === content);
    return adv?.measure || '—';
  };

  const renderCellContent = (measure: string, isSecondColumn: boolean) => {
    if (measure === 'true') {
      return isSecondColumn && isMobile ? (
        <img
          src={CheckedWhiteIcon}
          alt={measure}
        />
      ) : (
        <img
          src={CheckedTableIcon}
          alt={measure}
        />
      );
    }
    return measure;
  };

  const allAdvantages = getAllAdvantages();

  const handleNext = async (values?: Values, setFieldError?: (field: string, message: string | undefined) => void) => {
    if (activeStep === 0) {
      try {
        setFieldError?.('username', undefined);
        await signUpComplete({
          username: values?.username || userData?.username || '',
          gender: values?.gender || userData?.gender || '',
          age_group: values?.agegroup || userData?.age_group || '',
          country: values?.country || userData?.country || '',
        }).unwrap();
        if (values) {
          dispatch(
            setUserInfo({
              ...existingUser,
              ...(userData ?? {}),
              username: values.username,
              gender: values.gender,
              age_group: values.agegroup,
              country: values.country,
            })
          );
        }
      } catch (error) {
        console.error('Error completing sign up:', error);
        const apiErrors = (error as any)?.data?.errors;
        const usernameApiError = Array.isArray(apiErrors) ? apiErrors.find((e: any) => e?.attr === 'username') : null;
        if (usernameApiError?.detail) {
          setFieldError?.('username', String(usernameApiError.detail));
        }
        return;
      }
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleSubmitNext = async (_values: Values) => {
    setAcknowledge(false);
    try {
      if (premiumPlanId) {
        const data = await subscriptionCheckout({ plan_id: premiumPlanId }).unwrap();
        localStorage.setItem(PENDING_PREMIUM_CHECKOUT_KEY, String(Date.now()));
        if (data.checkout_url) {
          window.location.assign(data.checkout_url);
        }
        dispatch(setSelectedPlanId({ id: premiumPlanId }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleComplete = () => {};

  const handlePlanClick = async (id: number) => {
    try {
      if (id === premiumPlanId) {
        setAcknowledge(true);
      } else {
        await updatePlanMutation({ plan_id: id });
        navigate('/profile');
      }
      dispatch(setSelectedPlanId({ id }));
    } catch (error) {
      console.error(error);
    }
  };

  const initialValues = useMemo(() => {
    const defaultPlanId = data?.find((plan: IPlan) => plan.type === 'Premium')?.id ?? 1;

    return {
      username: userData?.username ?? '',
      gender: userData?.gender ?? '',
      agegroup: userData?.age_group ?? '',
      country: userData?.country ?? '',
      nearestCity: '',
      nearestAirport: '',
      unitsofMeasure: '',
      travel: '',
      collaborate: '',
      plan: defaultPlanId,
      budgetPreferences: '',
      pace: '',
      planningStyle: '',
      travelPurpose: [''],
      destinations: [''],
      interests: [''],
    };
  }, [userData, data]);

  const handleSubmit = async (values: Values) => {
    try {
      const primary_travel_purposes = (values.travelPurpose ?? [])[0]?.length ? values.travelPurpose : [];
      const destination_types = (values.destinations ?? [])[0]?.length ? values.destinations : [];
      const activity_interests = (values.interests ?? [])[0]?.length ? values.interests : [];
      const unit_system = values.unitsofMeasure === 'Miles/°F' ? 'Metric' : 'Imperial';

      await updateUserAdditionalInfo({
        nearest_city: values.nearestCity,
        nearest_airport: values.nearestAirport,
        unit_system,
        travel_preference: values.travel?.split(' ')[0],
        is_inviting_others: values.collaborate === 'yes',
        budget_preference: values.budgetPreferences,
        pace_preference: values.pace,
        primary_travel_purposes,
        destination_types,
        activity_interests,
        trip_planning_style: values.planningStyle,
      });

      const profileBase = userData ?? existingUser;
      dispatch(
        setUserInfo({
          ...profileBase,
          username: values.username,
          gender: values.gender,
          age_group: values.agegroup,
          country: values.country,
          nearest_city: values.nearestCity,
          nearest_airport: values.nearestAirport,
          unit_system,
          travel_preference: values.travel?.split(' ')[0] ?? '',
          is_inviting_others: values.collaborate === 'yes',
          budget_preference: values.budgetPreferences,
          pace_preference: values.pace,
          primary_travel_purposes,
          destination_types,
          activity_interests,
          trip_planning_style: values.planningStyle,
        })
      );

      const authPayload =
        userInfo ??
        (() => {
          try {
            const raw = localStorage.getItem('userInfo');
            return raw ? JSON.parse(raw) : null;
          } catch {
            return null;
          }
        })();

      if (authPayload?.access) {
        localStorage.setItem('userInfo', JSON.stringify(authPayload));
        localStorage.setItem('authToken', authPayload.access);
        dispatch(setCredentials(authPayload));
      }

      navigate('/profile');
    } catch (error) {
      console.error('Error during onboarding submission:', error);
    }
  };

  const formikValidate = (values: Values): FormikErrors<Values> => {
    const errors: FormikErrors<Values> = {};
    if (!values.username) {
      errors.username = 'Username is required';
    }
    if (!values.gender && !values.username) {
      errors.gender = 'Gender is required';
    }
    if (!values.agegroup && !values.gender && !values.username) {
      errors.agegroup = 'Age group is required';
    }
    if (!values.country && !values.agegroup && !values.gender && !values.username) {
      errors.country = 'Country is required';
    }
    return errors;
  };

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;

  return (
    <Container
      maxWidth={false}
      sx={{ padding: '0 !important' }}
    >
      <Box className={styles.personalizedForm}>
        <Box className={styles.header}>
          <Box className={styles.logoContainer}>
            <Box
              className={styles.logo}
              onClick={() => navigate('/')}
            >
              <Typography variant="h6Bold">Trav</Typography>
              <img
                src={LogoIcon}
                className={styles.logo}
                alt="logo"
              />
              <Typography variant="h6Bold">Genius</Typography>
            </Box>
            {/* <CustomButton
              variant='contained'
              customClass={activeStep < 2 ? styles.hideUpgradeBtn : styles.upgradeBtn}
              onClick={() => handleModalOpen(null)}
            >
              Upgrade Plan
            </CustomButton> */}
          </Box>
          <Box className={styles.progressContainer}>
            <Box
              sx={{
                height: '10px',
                backgroundColor: '#D9D9D9',
                width: '100%',
                borderRadius: '5px',
                margin: '10px 0px',
              }}
            >
              <Box
                sx={{
                  height: '10px',
                  background:
                    'linear-gradient(90deg, #51B888 0%, #17B2E8 100%), linear-gradient(269.95deg, #15C3FF 3.54%, #8DF4DB 100%)',
                  width: `${((activeStep + 1) * 100) / steps.length}%`,
                  borderRadius: '5px',
                }}
              />
            </Box>
            <Typography
              variant="h10"
              className={styles.steps}
            >
              {activeStep + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>

        <Box className={styles.formWrapper}>
          <Box className={`${styles.imagesContainer}  ${activeStep !== 0 && styles.hideImagesContainer}`}>
            <img
              src={Flamingo}
              style={{ position: 'absolute', bottom: 0, left: 0, maxWidth: '393px', width: '100%', zIndex: '-1' }}
              alt="Flamingo"
            />
          </Box>
          <Formik
            initialValues={initialValues}
            validate={formikValidate}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, handleChange, setFieldValue, setFieldError, isSubmitting }) => (
              <Form className={styles.stepsForm}>
                <Modal
                  open={openModal.isOpen}
                  onClose={handleModalClose}
                  aria-labelledby="plans"
                  aria-describedby="plans"
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      maxWidth: 1100,
                      width: '100%',
                      bgcolor: 'background.paper',
                      boxShadow: 24,
                      borderRadius: '20px',
                      p: isMobile ? '16px' : 4,
                      overflowY: 'auto',
                      maxHeight: '90vh',
                      paddingTop: isMobile ? '40px' : '',
                    }}
                  >
                    <Box sx={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer' }}>
                      <CloseIcon onClick={handleModalClose} />
                    </Box>
                    {openModal.content === null ? (
                      <Plans
                        values={values}
                        onChangePlan={handleChange}
                        onPlanAction={handlePlanClick}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ maxHeight: isMobile ? '' : '150px' }}>
                                <TableCell
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    textAlign: 'center',
                                    justifyContent: 'center',
                                    borderRight: '1px solid #BDC9EE',
                                    borderBottom: 'none',
                                    p: isMobile ? '4px 4px' : '',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      textAlign: 'center',
                                      minHeight: isMobile ? '50px' : '120px',
                                    }}
                                  >
                                    <Typography variant={isMobile ? 'h11Bold' : 'h5Bold'}>
                                      Find the right plan for you
                                    </Typography>
                                    {!isMobile && (
                                      <Typography
                                        variant="h10"
                                        sx={{ color: '#5E6A81' }}
                                      >
                                        Upgrade to experience the full power of TravGenius
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                {openModal.content.map((plan: IPlan, idx: number) => (
                                  <TableCell
                                    key={plan.id}
                                    align="center"
                                    sx={{
                                      borderRight: `1px solid ${
                                        idx === 1 ? '#30CC7E' : idx === plans.length - 1 ? 'transparent' : '#00ADE9'
                                      }`,
                                      borderBottom: 'none',
                                      p: isMobile ? '15px 4px 4px 4px' : '',
                                      backgroundColor: idx === 1 && isMobile ? '#EAEEF5' : '#fff',
                                    }}
                                  >
                                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                      <Typography
                                        variant={isMobile ? 'h11Bold' : 'h4Bold'}
                                        className={styles.title}
                                      >
                                        {plan.type}
                                      </Typography>
                                    </Box>
                                    {!isMobile && (
                                      <>
                                        <PlanPrice
                                          plan={plan}
                                          currencySymbol="$"
                                          className={styles.payment}
                                        />
                                        <Typography
                                          variant="h10Bold"
                                          className={plan.id === planId ? styles.selectedTerms : styles.terms}
                                        >
                                          {plan.type === 'Basic' ? 'Get started' : 'Start 3-day trial'}
                                        </Typography>
                                      </>
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {allAdvantages.map((content, index) => (
                                <TableRow key={index}>
                                  <TableCell
                                    sx={{
                                      borderRight: '1px solid #BDC9EE',
                                      borderBottom: 'none',
                                      p: isMobile ? '4px 1px' : '',
                                    }}
                                  >
                                    <Typography
                                      variant="h12"
                                      sx={{ color: isMobile ? '#222B3A' : '#5E6A81' }}
                                    >
                                      {content}
                                    </Typography>
                                  </TableCell>
                                  {plans?.map((plan: IPlan, idx: number) => {
                                    const measure = getMeasure(plan, content);
                                    return (
                                      <TableCell
                                        key={plan.id}
                                        align="center"
                                        sx={{
                                          borderRight: `1px solid ${
                                            idx === 1 ? '#30CC7E' : idx === plans.length - 1 ? 'transparent' : '#00ADE9'
                                          }`,
                                          borderBottom: 'none',
                                          p: isMobile ? '4px' : '',
                                          backgroundColor: idx === 1 && isMobile ? '#EAEEF5' : '#fff',
                                        }}
                                      >
                                        <Typography
                                          variant="h12"
                                          sx={{ color: '#5E6A81' }}
                                        >
                                          {renderCellContent(measure, idx === 1)}
                                        </Typography>
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Box>
                </Modal>
                <Modal
                  open={acknowledge}
                  onClose={() => setAcknowledge(false)}
                  aria-labelledby="acknowledge"
                  aria-describedby="acknowledge"
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      maxWidth: 870,
                      width: '100%',
                      outline: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: '20px',
                        padding: '30px 80px',
                        overflowY: 'auto',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '20px',
                      }}
                    >
                      <IconButton
                        type="button"
                        aria-label="Close disclaimer"
                        onClick={() => setAcknowledge(false)}
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          color: '#222B3A',
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 28 }} />
                      </IconButton>
                      <img
                        src={RoboHandImg}
                        alt="checked"
                      />
                      <Typography
                        variant="h5Bold"
                        className={styles.acknowledgeTitle}
                        sx={{ color: '#222B3A' }}
                      >
                        AI-Powered Features Disclaimer
                      </Typography>
                      <Typography
                        variant="h10"
                        sx={{ color: '#222B3A', textAlign: 'center' }}
                      >
                        TravGenius enhances your travel experience with smart tools and AI-powered features, but some
                        functionalities are still evolving and may not always be perfect. Results may be inaccurate or
                        unavailable at times. Use TravGenius as a guide, but always verify important details and keep
                        copies of essential documents.
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                          mt: 1,
                        }}
                      >
                        <CustomButton
                          variant="outlined"
                          btnType="button"
                          onClick={() => setAcknowledge(false)}
                          customClass={styles.btn}
                        >
                          Cancel
                        </CustomButton>
                        <CustomButton
                          btnType="button"
                          onClick={() => handleSubmitNext(values)}
                          customClass={styles.btn}
                        >
                          Agree
                        </CustomButton>
                      </Box>
                    </Box>
                  </Box>
                </Modal>
                {/* step 1 */}
                <Box
                  sx={{ display: `${activeStep === 0 ? 'flex' : 'none'}`, marginTop: '50px' }}
                  className={styles.stepWrapper}
                >
                  <Typography
                    variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`}
                    sx={{ textAlign: 'center', color: '#222B3A' }}
                  >
                    Personalized travel starts here!
                  </Typography>
                  <Typography
                    variant="h10"
                    className={styles.stepSubtitle}
                  >
                    Tell us a bit about yourself for a tailored journey
                  </Typography>
                  <Box className={styles.stepFields}>
                    <CustomInput
                      inputType="text"
                      inputName="username"
                      required
                      inputLabel="Username"
                      placeholder="Create your username"
                      value={values.username}
                      error={fieldErrorMessage(errors.username)}
                      customClass={styles.customInput}
                      onChange={handleChange}
                    />
                    <CustomSelect
                      name="gender"
                      inputLabel="Gender"
                      handleChange={handleChange}
                      required={true}
                      value={values.gender}
                      data={['Male', 'Female', 'Non-binary', 'Prefer not to say']}
                      labelByValue={{ 'Non-binary': 'Non-binary/Other' }}
                      placeholder="Choose your gender"
                    />
                    <CustomSelect
                      name="agegroup"
                      inputLabel="Age group"
                      handleChange={handleChange}
                      required={true}
                      value={values.agegroup}
                      data={['18-24', '25-34', '35-44', '45-54', '55-64', '65+']}
                      placeholder="Choose your age range"
                    />
                    <CustomAutoSelect
                      options={countries ?? []}
                      inputLabel="Country"
                      placeholder="Enter country name"
                      handleChange={(e, value) => setFieldValue('country', value ? value.name : '')}
                      onInputChange={(event, inputValue) => {
                        setCountryInput(inputValue);
                      }}
                      value={values.country ? { name: values.country } : null}
                    />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Box className={styles.stepsButtons}>
                      <CustomButton
                        variant="outlined"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      >
                        Back
                      </CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant="contained"
                          disabled={!values.username?.trim() || !values.gender || !values.agegroup || !values.country}
                          onClick={isLastStep ? handleComplete : () => handleNext(values, setFieldError)}
                        >
                          Next
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 2 */}
                <Box sx={{ display: `${activeStep === 1 ? 'block' : 'none'}`, marginTop: '50px' }}>
                  <Plans
                    values={values}
                    onChangePlan={handleChange}
                    onPlanAction={handlePlanClick}
                  />

                  <Box className={styles.stepNavPlan}>
                    <Box className={styles.planStepsButtons}>
                      <CustomButton
                        variant="outlined"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      >
                        Back
                      </CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant="contained"
                          disabled={!values.username?.trim() || !values.gender || !values.agegroup || !values.country}
                          onClick={() => handlePlanClick(Number(values.plan))}
                        >
                          Next
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 3 */}
                <Box
                  sx={{ display: `${activeStep === 2 ? 'flex' : 'none'}`, marginTop: '50px', position: 'relative' }}
                  className={styles.stepWrapper}
                >
                  <img
                    src={SecondBgImg}
                    style={{
                      display: isMobile ? 'none' : 'block',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      maxWidth: '393px',
                      width: '100%',
                      zIndex: '-1',
                    }}
                    alt="SecondBgImg"
                  />
                  <Typography
                    variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`}
                    className={styles.stepTitle}
                  >
                    Let's personalize your AI assistant
                  </Typography>
                  <Typography
                    variant="h10"
                    className={styles.stepSubtitle}
                  >
                    Just a few quick answers to give you the best experience
                  </Typography>
                  <Box className={styles.stepFields}>
                    <CustomAutoSelect
                      options={cities ?? []}
                      inputLabel="Nearest City"
                      placeholder="Enter city"
                      handleChange={(e, value) => setFieldValue('nearestCity', value ? value.name : '')}
                      onInputChange={(event, inputValue) => {
                        setCityInput(inputValue);
                      }}
                      value={values.nearestCity ? { name: values.nearestCity } : null}
                    />
                    <CustomAutoSelect
                      options={airports ?? []}
                      inputLabel="Nearest Airport"
                      placeholder="Enter airport"
                      handleChange={(e, value) => setFieldValue('nearestAirport', value ? value.name : '')}
                      onInputChange={(event, inputValue) => {
                        setAirportInput(inputValue);
                      }}
                      value={values.nearestAirport ? { name: values.nearestAirport } : null}
                    />
                    <CustomSelect
                      name="unitsofMeasure"
                      inputLabel="Units of Measure"
                      handleChange={handleChange}
                      value={values.unitsofMeasure}
                      data={['Miles/°F', 'Km/°C']}
                    />
                  </Box>
                  <Box className={styles.additionalInfo}>
                    <Typography
                      variant="h11"
                      className={styles.additionalInfoText}
                    >
                      To get badges, fill in all the questions
                    </Typography>
                    <img
                      src={InformationIcon}
                      className={styles.additionalInfoImg}
                      alt="info"
                    />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography
                      variant="h10Semibold"
                      onClick={() => handleSubmit(values)}
                      className={styles.stepBtnSubtitle}
                    >
                      Personalize later
                    </Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton
                        variant="outlined"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      >
                        Back
                      </CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant="contained"
                          disabled={!values.username?.trim() || !values.gender || !values.agegroup || !values.country}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {!values.nearestCity && !values.unitsofMeasure ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 4 */}
                <Box
                  sx={{ display: `${activeStep === 3 ? 'flex' : 'none'}`, marginTop: '50px', position: 'relative' }}
                  className={styles.stepWrapper}
                >
                  <img
                    src={ThirdBgImg}
                    style={{
                      display: isMobile ? 'none' : 'block',
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      maxWidth: '393px',
                      width: '100%',
                      zIndex: '-1',
                    }}
                    alt="ThirdBgImg"
                  />
                  <FormControl
                    component="fieldset"
                    className={styles.radioGroupWrapper}
                  >
                    <Typography
                      variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`}
                      className={styles.stepTitle}
                    >
                      Let's personalize your AI assistant
                    </Typography>
                    <Typography
                      variant="h10"
                      className={styles.stepSubtitle}
                    >
                      How do you travel?
                    </Typography>
                    <RadioGroup
                      name="travel"
                      className={styles.radioGroup}
                      value={values.travel}
                      onChange={handleChange}
                    >
                      {[
                        { name: 'Solo travel', IconComponent: SoloIcon, iconSize: 45 },
                        { name: 'Couples travel', IconComponent: LikeIcon, iconSize: 45 },
                        { name: 'Family travel', IconComponent: FamilyIcon, iconSize: 45 },
                        { name: 'Friends travel', IconComponent: FriendsIcon, iconSize: 45 },
                        { name: 'Work travel', IconComponent: WorkTravelIcon, iconSize: 45 },
                      ].map(val => {
                        const { IconComponent, iconSize } = val;
                        const isSelected = values.travel === val.name;
                        return (
                          <FormControlLabel
                            key={val.name}
                            value={val.name}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '16px',
                                  ...(isSelected
                                    ? {
                                        background:
                                          'linear-gradient(white, white) padding-box, linear-gradient(231.69deg, #15C3FF 20.67%, #8DF4DB 100%) border-box',
                                        border: '2px solid transparent',
                                      }
                                    : {
                                        border: '1px solid #BDC9EE',
                                      }),
                                  borderRadius: '12px',
                                  backgroundColor: '#fff',
                                  cursor: 'pointer',
                                  width: '200px',
                                  height: '130px',
                                  gap: '8px',
                                }}
                              >
                                <IconComponent
                                  size={iconSize}
                                  color={isSelected ? undefined : '#BDC9EE'}
                                  gradient={isSelected ? generateActiveGradient(val.name, 'travel') : undefined}
                                />
                                <Typography
                                  variant="h10"
                                  sx={{ textAlign: 'center', fontSize: '18px' }}
                                >
                                  {val.name.replace('_', ' ')}
                                </Typography>
                              </Box>
                            }
                          />
                        );
                      })}
                    </RadioGroup>
                  </FormControl>

                  <FormControl
                    component="fieldset"
                    className={`${styles.radioGroupWrapper} ${styles.mt35}`}
                  >
                    <FormLabel
                      component="legend"
                      className={styles.formLabel}
                    >
                      Do you plan to invite others to collaborate?
                    </FormLabel>
                    <RadioGroup
                      name="collaborate"
                      className={styles.radioGroup}
                      value={values.collaborate}
                      onChange={handleChange}
                    >
                      {['Yes', 'No'].map(val => (
                        <FormControlLabel
                          key={val}
                          value={val}
                          control={<Radio sx={{ display: 'none' }} />}
                          label={
                            <ToggleRadioButton
                              value={val}
                              label={val}
                              selected={values.collaborate === val}
                              yesNo
                            />
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography
                      variant="h11"
                      className={styles.additionalInfoText}
                    >
                      To get badges, fill in all the questions
                    </Typography>
                    <img
                      src={InformationIcon}
                      className={styles.additionalInfoImg}
                      alt="info"
                    />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography
                      variant="h10Semibold"
                      onClick={() => handleSubmit(values)}
                      className={styles.stepBtnSubtitle}
                    >
                      Personalize later
                    </Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton
                        variant="outlined"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      >
                        Back
                      </CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant="contained"
                          disabled={!values.username?.trim() || !values.gender || !values.agegroup || !values.country}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {!values.travel && !values.collaborate ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 5 */}
                <Box
                  sx={{ display: `${activeStep === 4 ? 'flex' : 'none'}`, marginTop: '50px' }}
                  className={styles.stepWrapper}
                >
                  <img
                    src={FourthBgImg}
                    style={{
                      display: isMobile ? 'none' : 'block',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      maxWidth: '393px',
                      width: '100%',
                      zIndex: '-1',
                    }}
                    alt="FourthBgImg"
                  />
                  <FormControl
                    component="fieldset"
                    className={styles.radioGroupWrapper}
                  >
                    <Typography
                      variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`}
                      className={styles.stepTitle}
                    >
                      Let's personalize your AI assistant
                    </Typography>
                    <Typography
                      variant="h10"
                      className={styles.stepSubtitle}
                    >
                      Budget Preferences
                    </Typography>
                    <RadioGroup
                      name="budgetPreferences"
                      className={styles.radioGroup}
                      value={values.budgetPreferences}
                      onChange={handleChange}
                    >
                      {[
                        { value: 'Economy', name: 'Economy', IconComponent: EconomyIcon, iconSize: 45 },
                        { value: 'Mid-range', name: 'Mid-Range', IconComponent: MidRangeIcon, iconSize: 45 },
                        { value: 'Luxury', name: 'Luxury', IconComponent: LuxuryIcon, iconSize: 45 },
                        {
                          value: "Doesn't matter",
                          name: "Doesn't Matter",
                          IconComponent: DoesntMatterIcon,
                          iconSize: 45,
                        },
                      ].map(val => {
                        const { IconComponent, iconSize } = val;
                        const isSelected = values.budgetPreferences === val.value;
                        return (
                          <FormControlLabel
                            key={val.value}
                            value={val.value}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '16px',
                                  ...(isSelected
                                    ? {
                                        background:
                                          'linear-gradient(white, white) padding-box, linear-gradient(231.69deg, #15C3FF 20.67%, #8DF4DB 100%) border-box',
                                        border: '2px solid transparent',
                                      }
                                    : {
                                        border: '1px solid #BDC9EE',
                                      }),
                                  borderRadius: '12px',
                                  backgroundColor: '#fff',
                                  cursor: 'pointer',
                                  width: '200px',
                                  height: '130px',
                                  gap: '8px',
                                }}
                              >
                                <IconComponent
                                  size={iconSize}
                                  color={isSelected ? undefined : '#C2E2FF'}
                                  gradient={isSelected ? generateActiveGradient(val.value, 'budget') : undefined}
                                />
                                <Typography
                                  variant="h10"
                                  sx={{ textAlign: 'center', fontSize: '18px' }}
                                >
                                  {val.name.replace('_', ' ')}
                                </Typography>
                              </Box>
                            }
                          />
                        );
                      })}
                    </RadioGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography
                      variant="h11"
                      className={styles.additionalInfoText}
                    >
                      To get badges, fill in all the questions
                    </Typography>
                    <img
                      src={InformationIcon}
                      className={styles.additionalInfoImg}
                      alt="info"
                    />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography
                      variant="h10Semibold"
                      onClick={() => handleSubmit(values)}
                      className={styles.stepBtnSubtitle}
                    >
                      Personalize later
                    </Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton
                        variant="outlined"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      >
                        Back
                      </CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant="contained"
                          disabled={!values.username?.trim() || !values.gender || !values.agegroup || !values.country}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {!values.budgetPreferences ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 6 */}
                <Box
                  sx={{ display: `${activeStep === 5 ? 'flex' : 'none'}`, marginTop: '50px' }}
                  className={styles.stepWrapper}
                >
                  <img
                    src={FifthBgImg}
                    style={{
                      display: isMobile ? 'none' : 'block',
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      maxWidth: '393px',
                      width: '100%',
                      zIndex: '-1',
                    }}
                    alt="FifthBgImg"
                  />
                  <FormControl
                    component="fieldset"
                    className={styles.radioGroupWrapper}
                  >
                    <Typography
                      variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`}
                      className={styles.stepTitle}
                    >
                      Let's personalize your AI assistant
                    </Typography>
                    <Typography
                      variant="h10"
                      className={styles.stepSubtitle}
                    >
                      Preferred Pace
                    </Typography>
                    <RadioGroup
                      name="pace"
                      className={styles.radioGroup}
                      value={values.pace}
                      onChange={handleChange}
                    >
                      {[
                        { value: 'Fast-paced', name: 'Fast-Paced', IconComponent: FastPacedIcon, iconSize: 45 },
                        { value: 'Balanced', name: 'Balanced', IconComponent: BalancedIcon, iconSize: 45 },
                        {
                          value: 'Slow & Relaxed',
                          name: 'Slow & Relaxed',
                          IconComponent: SlowAndRelaxedIcon,
                          iconSize: 45,
                        },
                      ].map(val => {
                        const { IconComponent, iconSize } = val;
                        const isSelected = values.pace === val.value;
                        return (
                          <FormControlLabel
                            key={val.value}
                            value={val.value}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '16px',
                                  ...(isSelected
                                    ? {
                                        background:
                                          'linear-gradient(white, white) padding-box, linear-gradient(231.69deg, #15C3FF 20.67%, #8DF4DB 100%) border-box',
                                        border: '2px solid transparent',
                                      }
                                    : {
                                        border: '1px solid #BDC9EE',
                                      }),
                                  borderRadius: '12px',
                                  backgroundColor: '#fff',
                                  cursor: 'pointer',
                                  width: '200px',
                                  height: '130px',
                                  gap: '8px',
                                }}
                              >
                                <IconComponent
                                  size={iconSize}
                                  color={isSelected ? undefined : '#C2E2FF'}
                                  gradient={isSelected ? generateActiveGradient(val.value, 'pace') : undefined}
                                />
                                <Typography
                                  variant="h10"
                                  sx={{ textAlign: 'center', fontSize: '18px' }}
                                >
                                  {val.name.replace('_', ' ')}
                                </Typography>
                              </Box>
                            }
                          />
                        );
                      })}
                    </RadioGroup>
                  </FormControl>

                  <FormControl
                    component="fieldset"
                    className={`${styles.radioGroupWrapper} ${styles.mt35}`}
                  >
                    <FormLabel
                      component="legend"
                      sx={{ color: '#5E6A81' }}
                    >
                      Trip Planning Style
                    </FormLabel>
                    <RadioGroup
                      name="planningStyle"
                      className={styles.radioGroup}
                      value={values.planningStyle}
                      onChange={handleChange}
                    >
                      {['Structured', 'Flexible', 'Spontaneous'].map(val => (
                        <FormControlLabel
                          key={val}
                          value={val}
                          control={<Radio sx={{ display: 'none' }} />}
                          label={
                            <ToggleRadioButton
                              value={val}
                              label={val}
                              selected={values.planningStyle === val}
                            />
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography
                      variant="h11"
                      className={styles.additionalInfoText}
                    >
                      To get badges, fill in all the questions
                    </Typography>
                    <img
                      src={InformationIcon}
                      className={styles.additionalInfoImg}
                      alt="info"
                    />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography
                      variant="h10Semibold"
                      onClick={() => handleSubmit(values)}
                      className={styles.stepBtnSubtitle}
                    >
                      Personalize later
                    </Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton
                        variant="outlined"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      >
                        Back
                      </CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant="contained"
                          disabled={!values.username?.trim() || !values.gender || !values.agegroup || !values.country}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {!values.pace && !values.planningStyle ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 7 */}
                <Box
                  sx={{ display: `${activeStep === 6 ? 'flex' : 'none'}`, marginTop: '50px', position: 'relative' }}
                  className={styles.stepWrapper}
                >
                  <img
                    src={PhotoaparatBgImg}
                    style={{
                      display: isMobile ? 'none' : 'block',
                      position: 'absolute',
                      bottom: '20%',
                      left: 0,
                      maxWidth: '285px',
                      width: '100%',
                      zIndex: '-1',
                    }}
                    alt="PhotoaparatBgImg"
                  />
                  <FormControl
                    component="fieldset"
                    className={`${styles.radioGroupWrapper} ${styles.mt35}`}
                  >
                    <Typography
                      variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`}
                      className={styles.stepTitle}
                    >
                      Let's personalize your AI assistant
                    </Typography>
                    <Typography
                      variant="h10"
                      className={styles.stepSubtitle}
                    >
                      Primary Travel Purpose (Select Upto 6)
                    </Typography>
                    <FormGroup className={styles.checkboxesGroup}>
                      {primaryTravelPurpose?.map((option: { name: string; is_free: boolean }) => {
                        const isChecked = values.travelPurpose.includes(option.name);
                        const finalDisabled = values.travelPurpose.length >= MAX_SELECTABLE_OPTIONS && !isChecked;

                        return (
                          <FormControlLabel
                            key={option.name}
                            className={`${styles.checkLabel} ${isChecked ? styles.checked : ''} ${
                              finalDisabled ? styles.disabledOption : ''
                            }`}
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={isChecked}
                                disabled={finalDisabled}
                                onChange={event => {
                                  if (event.target.checked) {
                                    setFieldValue(
                                      'travelPurpose',
                                      !!values.travelPurpose[0]?.length
                                        ? [...values.travelPurpose, option.name]
                                        : [option.name]
                                    );
                                  } else {
                                    setFieldValue(
                                      'travelPurpose',
                                      values.travelPurpose.filter((item: string) => item !== option.name)
                                    );
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant={isChecked ? 'h10Semibold' : 'h10'}
                                className={finalDisabled ? styles.disabledOptionText : ''}
                              >
                                {option.name}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography
                      variant="h11"
                      className={styles.additionalInfoText}
                    >
                      To get badges, fill in all the questions
                    </Typography>
                    <img
                      src={InformationIcon}
                      className={styles.additionalInfoImg}
                      alt="info"
                    />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography
                      variant="h10Semibold"
                      onClick={() => handleSubmit(values)}
                      className={styles.stepBtnSubtitle}
                    >
                      Personalize later
                    </Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton
                        variant="outlined"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      >
                        Back
                      </CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant="contained"
                          disabled={!values.username?.trim() || !values.gender || !values.agegroup || !values.country}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {values.travelPurpose.length === 0 || values.travelPurpose[0].length === 0 ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 8 */}
                <Box
                  sx={{ display: `${activeStep === 7 ? 'flex' : 'none'}`, marginTop: '50px' }}
                  className={styles.stepWrapper}
                >
                  <img
                    src={BikeBgImg}
                    style={{
                      display: isMobile ? 'none' : 'block',
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      maxWidth: '291px',
                      width: '100%',
                      zIndex: '-1',
                    }}
                    alt="BikeBgImg"
                  />
                  <FormControl
                    component="fieldset"
                    className={`${styles.radioGroupWrapper} ${styles.mt35}`}
                  >
                    <Typography
                      variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`}
                      className={styles.stepTitle}
                    >
                      Let's personalize your AI assistant
                    </Typography>
                    <Typography
                      variant="h10"
                      className={styles.stepSubtitle}
                    >
                      Activity Interests (Select Upto 6)
                    </Typography>
                    <FormGroup className={styles.checkboxesGroup}>
                      {activityInterest?.map((option: { name: string; is_free: boolean }) => {
                        const isChecked = values.interests.includes(option.name);
                        const finalDisabled = values.interests.length >= MAX_SELECTABLE_OPTIONS && !isChecked;

                        return (
                          <FormControlLabel
                            key={option.name}
                            className={`${styles.checkLabel} ${isChecked ? styles.checked : ''} ${
                              finalDisabled ? styles.disabledOption : ''
                            }`}
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={isChecked}
                                disabled={finalDisabled}
                                onChange={event => {
                                  if (event.target.checked) {
                                    setFieldValue(
                                      'interests',
                                      !!values.interests[0]?.length ? [...values.interests, option.name] : [option.name]
                                    );
                                  } else {
                                    setFieldValue(
                                      'interests',
                                      values.interests.filter((item: string) => item !== option.name)
                                    );
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant={isChecked ? 'h10Semibold' : 'h10'}
                                className={finalDisabled ? styles.disabledOptionText : ''}
                              >
                                {option.name}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography
                      variant="h11"
                      className={styles.additionalInfoText}
                    >
                      To get badges, fill in all the questions
                    </Typography>
                    <img
                      src={InformationIcon}
                      className={styles.additionalInfoImg}
                      alt="info"
                    />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography
                      variant="h10Semibold"
                      onClick={() => handleSubmit(values)}
                      className={styles.stepBtnSubtitle}
                    >
                      Personalize later
                    </Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton
                        variant="outlined"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      >
                        Back
                      </CustomButton>
                      {!isLastStep && (
                        <CustomButton
                          variant="contained"
                          disabled={!values.username?.trim() || !values.gender || !values.agegroup || !values.country}
                          onClick={isLastStep ? handleComplete : handleNext}
                        >
                          {values.destinations.length === 0 || values.destinations[0].length === 0 ? 'Skip' : 'Next'}
                        </CustomButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* step 9 */}
                <Box
                  sx={{ display: `${activeStep === 8 ? 'flex' : 'none'}`, marginTop: '50px', position: 'relative' }}
                  className={styles.stepWrapper}
                >
                  <img
                    src={EiffelTowerBgImg}
                    style={{
                      display: isMobile ? 'none' : 'block',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      maxWidth: '275px',
                      width: '100%',
                      zIndex: '-1',
                    }}
                    alt="EiffelTowerBgImg"
                  />
                  <FormControl
                    component="fieldset"
                    className={`${styles.radioGroupWrapper} ${styles.mt35}`}
                  >
                    <Typography
                      variant={`${isMobile ? 'h9Bold' : 'h5Bold'}`}
                      className={styles.stepTitle}
                    >
                      Let's personalize your AI assistant
                    </Typography>
                    <Typography
                      variant="h10"
                      className={styles.stepSubtitle}
                    >
                      Types Of Destinations (Select Upto 6)
                    </Typography>
                    <FormGroup className={styles.checkboxesGroup}>
                      {typeOfDestination?.map((option: { name: string; is_free: boolean }) => {
                        const isChecked = values.destinations.includes(option.name);
                        const finalDisabled = values.destinations.length >= MAX_SELECTABLE_OPTIONS && !isChecked;

                        return (
                          <FormControlLabel
                            key={option.name}
                            className={`${styles.checkLabel} ${isChecked ? styles.checked : ''} ${
                              finalDisabled ? styles.disabledOption : ''
                            }`}
                            control={
                              <Checkbox
                                className={styles.checkbox}
                                checked={isChecked}
                                disabled={finalDisabled}
                                onChange={event => {
                                  if (event.target.checked) {
                                    setFieldValue(
                                      'destinations',
                                      !!values.destinations[0]?.length
                                        ? [...values.destinations, option.name]
                                        : [option.name]
                                    );
                                  } else {
                                    setFieldValue(
                                      'destinations',
                                      values.destinations.filter((item: string) => item !== option.name)
                                    );
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant={isChecked ? 'h10Semibold' : 'h10'}
                                className={finalDisabled ? styles.disabledOptionText : ''}
                              >
                                {option.name}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </FormControl>

                  <Box className={styles.additionalInfo}>
                    <Typography
                      variant="h11"
                      className={styles.additionalInfoText}
                    >
                      To get badges, fill in all the questions
                    </Typography>
                    <img
                      src={InformationIcon}
                      className={styles.additionalInfoImg}
                      alt="info"
                    />
                  </Box>

                  <Box className={styles.stepNav}>
                    <Typography
                      variant="h10Semibold"
                      onClick={() => handleSubmit(values)}
                      className={styles.stepBtnSubtitle}
                    >
                      Personalize later
                    </Typography>
                    <Box className={styles.stepsButtons}>
                      <CustomButton
                        variant="outlined"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      >
                        Back
                      </CustomButton>
                      {isLastStep && <CustomButton btnType="submit">Finish</CustomButton>}
                    </Box>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Container>
  );
};

export default PersonalizedForm;
